import React from 'react';
import { motion } from 'framer-motion';

interface ChatMessageProps {
  text: string;
  timestamp: string;
  isFromUser: boolean;
  className?: string;
  showAuthor?: boolean;
  authorName?: string;
  isGroupedWithPrevious?: boolean;
  isManual?: boolean;
}

// Function to parse WhatsApp-style formatting
const parseWhatsAppFormat = (text: string): React.ReactNode[] => {
  // Split by newlines to handle line breaks and lists
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: React.ReactNode[] = [];
  let listType: 'bullet' | 'number' | null = null;

  lines.forEach((line, index) => {
    // Check for list items
    if (line.match(/^- /)) {
      // Bullet list
      if (listType !== 'bullet' && currentList.length > 0) {
        // Close previous list
        elements.push(<ul key={`list-${index}`} className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
        currentList = [];
      }
      listType = 'bullet';
      currentList.push(<li key={`item-${index}`}>{formatInlineText(line.slice(2))}</li>);
      return;
    }
    
    if (line.match(/^\d+\. /)) {
      // Numbered list
      if (listType !== 'number' && currentList.length > 0) {
        // Close previous list
        elements.push(<ul key={`list-${index}`} className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
        currentList = [];
      }
      listType = 'number';
      currentList.push(<li key={`item-${index}`}>{formatInlineText(line.slice(3))}</li>);
      return;
    }

    // Check for blockquote
    if (line.match(/^>/)) {
      // Flush any pending list
      if (currentList.length > 0) {
        if (listType === 'bullet') {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
        } else if (listType === 'number') {
          elements.push(<ol key={`list-${index}`} className="list-decimal list-inside ml-2 my-1">{currentList}</ol>);
        }
        currentList = [];
        listType = null;
      }
      
      elements.push(
        <blockquote key={`quote-${index}`} className="border-l-4 border-gray-400 pl-2 italic my-1">
          {formatInlineText(line.slice(1))}
        </blockquote>
      );
      return;
    }

    // Check for code block
    if (line.match(/^```/)) {
      if (currentList.length > 0) {
        if (listType === 'bullet') {
          elements.push(<ul key={`list-${index}`} className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
        } else if (listType === 'number') {
          elements.push(<ol key={`list-${index}`} className="list-decimal list-inside ml-2 my-1">{currentList}</ol>);
        }
        currentList = [];
        listType = null;
      }
      return;
    }

    // Regular line
    if (currentList.length > 0) {
      if (listType === 'bullet') {
        elements.push(<ul key={`list-${index}`} className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
      } else if (listType === 'number') {
        elements.push(<ol key={`list-${index}`} className="list-decimal list-inside ml-2 my-1">{currentList}</ol>);
      }
      currentList = [];
      listType = null;
    }

    if (line.trim()) {
      elements.push(
        <div key={`line-${index}`} className="my-1">
          {formatInlineText(line)}
        </div>
      );
    } else {
      elements.push(<br key={`br-${index}`} />);
    }
  });

  // Close any remaining list
  if (currentList.length > 0) {
    if (listType === 'bullet') {
      elements.push(<ul key="list-final" className="list-disc list-inside ml-2 my-1">{currentList}</ul>);
    } else if (listType === 'number') {
      elements.push(<ol key="list-final" className="list-decimal list-inside ml-2 my-1">{currentList}</ol>);
    }
  }

  return elements;
};

// Function to format inline text (bold, italic, strikethrough, code)
const formatInlineText = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  const regex = /(\*\*.*?\*\*|\*.*?\*|_.*?_|~.*?~|`.*?`)/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }

    const matched = match[0];
    // Determine type
    if (matched.startsWith('**')) {
      parts.push(<strong key={`bold-${currentIndex}`}>{matched.slice(2, -2)}</strong>);
    } else if (matched.startsWith('*')) {
      parts.push(<strong key={`bold-${currentIndex}`}>{matched.slice(1, -1)}</strong>);
    } else if (matched.startsWith('_')) {
      parts.push(<em key={`italic-${currentIndex}`}>{matched.slice(1, -1)}</em>);
    } else if (matched.startsWith('~')) {
      parts.push(<del key={`strike-${currentIndex}`}>{matched.slice(1, -1)}</del>);
    } else if (matched.startsWith('`')) {
      parts.push(<code key={`code-${currentIndex}`} className="bg-black/10 px-1 rounded">{matched.slice(1, -1)}</code>);
    }

    currentIndex = match.index + matched.length;
  }

  // Add remaining text
  if (currentIndex < text.length) {
    parts.push(text.substring(currentIndex));
  }

  return parts.length > 0 ? parts : [text];
};

export const DateSeparator: React.FC<{ dateLabel: string }> = ({ dateLabel }) => {
  return (
    <div className="sticky top-0 z-10 flex items-center justify-center w-full py-2">
      <div className="bg-black/5 px-3 py-1 rounded-full bg-muted">
        <p className="font-sans font-normal text-xs text-text-subtle">{dateLabel}</p>
      </div>
    </div>
  );
};

export const ChatMessage: React.FC<ChatMessageProps> = ({
  text,
  timestamp,
  isFromUser,
  className = '',
  showAuthor = false,
  authorName,
  isGroupedWithPrevious = false,
  isManual = false
}) => {
  const formattedContent = parseWhatsAppFormat(text);
  
  // Determine background color: #3C4441 for manual messages, #097B4A for AI messages
  const backgroundColor = isFromUser 
    ? (isManual ? 'bg-[#3C4441]' : 'bg-[#097B4A]')
    : 'bg-white border border-border';
  
  const borderRadius = isFromUser 
    ? 'rounded-2xl rounded-br-sm'
    : 'rounded-2xl rounded-bl-sm';

  return (
    <motion.div 
      className={`flex items-start gap-2 ${isFromUser ? 'justify-end' : 'justify-start'} w-full ${isGroupedWithPrevious ? 'mt-0.5' : 'mt-0'} ${className}`}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
        <div
          className={`max-w-[70%] px-3 py-2 ${backgroundColor} ${borderRadius}`}
        >
          {showAuthor && !isFromUser && authorName && (
            <p className="font-sans font-semibold text-xs text-primary mb-1">
              {authorName}
            </p>
          )}
          <div className={`font-sans font-normal text-sm ${
            isFromUser ? 'text-white' : 'text-text'
          } leading-relaxed`}>
            {formattedContent}
          </div>
          <p className={`font-sans font-normal text-xs mt-1 ${
            isFromUser ? 'text-white/70' : 'text-text-subtle'
          }`}>
            {timestamp}
          </p>
        </div>
      </motion.div>
  );
};

export { ChatMessage as default };

